import { ArticoloType, EditorColoreApiType, TecnicaType } from '@utils/types'
import {
  applyAlphaToHexColor,
  convertHexToPantone,
  normalizeHexColor,
  splitHexColorAndAlpha,
} from './index'

/**
 * API colore condivise tra editor e altri componenti.
 * Le logiche colore specifiche di `useFabricEditor` vivono in `fabric-editor-colors.ts`.
 */

export type EditorResolvedColorLimits = {
  hasColorLimits: boolean
  allowedColors: EditorColoreApiType[]
  lockColorChange: EditorColoreApiType | null
}

export const resolveEditorColorLimits = (
  tecnica?: TecnicaType | null,
  articoloPrincipale?: ArticoloType | null
): EditorResolvedColorLimits => {
  const isColorLimitedMode = !!tecnica?.editor_modalita_colori_fissi

  if (!isColorLimitedMode) {
    return {
      hasColorLimits: false,
      allowedColors: [],
      lockColorChange: null,
    }
  }

  const tecnicaPantoniConsentiti = Array.isArray(tecnica?.editor_pantoni_consentiti)
    ? tecnica.editor_pantoni_consentiti
    : []

  const allowedFromTecnicaPantoni = Array.from(
    new Set(
      tecnicaPantoniConsentiti
        .map((pantone: any) => normalizeHexColor(pantone?.hex || ''))
        .filter(Boolean)
    )
  )

  if (allowedFromTecnicaPantoni.length > 0) {
    return {
      hasColorLimits: true,
      allowedColors: tecnicaPantoniConsentiti,
      lockColorChange: tecnicaPantoniConsentiti?.length === 1 ? tecnicaPantoniConsentiti[0] : null,
    }
  }

  if (!!tecnica?.editor_forza_colore) {
    return {
      hasColorLimits: true,
      allowedColors: [tecnica?.editor_forza_colore],
      lockColorChange: tecnica?.editor_forza_colore,
    }
  }

  const articoloColoreByTecnica = articoloPrincipale?.editor_colori?.find(
    (c) => c.tecnica_id === tecnica.id
  )?.editor_colore

  const primaryEditorColor =
    articoloColoreByTecnica || articoloPrincipale?.editor_colore || tecnica?.editor_colore
  const fallbackEditorColor =
    tecnica?.editor_colore || articoloColoreByTecnica || articoloPrincipale?.editor_colore
  const allowedColors = [primaryEditorColor || fallbackEditorColor].filter(Boolean)

  return {
    hasColorLimits: allowedColors.length > 0,
    allowedColors,
    lockColorChange: allowedColors.length === 1 ? fallbackEditorColor : null,
  }
}

/**
 * Converte un HEX nell'HEX del Pantone più vicino.
 * Se la conversione fallisce restituisce il colore originale normalizzato.
 */
export const getPantoneHex = (hex: string) => {
  try {
    const sourceColor = splitHexColorAndAlpha(hex)
    if (!sourceColor) return normalizeHexColor(hex)

    const pantone = convertHexToPantone(hex)
    if (!pantone?.hex) return hex

    return applyAlphaToHexColor(pantone.hex, sourceColor.alphaHex)
  } catch (error) {
    console.warn(`Could not convert color ${hex} to Pantone`, error)
    return normalizeHexColor(hex)
  }
}
